import React, { useState } from 'react';
import { colors as theme } from '../theme';

const brandColors = {
  gold: theme.gold,
  darkGray: theme.surface1,
  mediumGray: theme.surface2,
  lightGray: theme.surface3,
  silver: theme.textMuted,
  white: theme.text,
};

// Organized muscle groups for body highlighting
const MUSCLE_GROUPS = {
  'Upper Body - Front': [
    'Chest', 'Upper Chest', 'Lower Chest', 'Pectoralis Major',
    'Front Delts', 'Side Delts',
    'Biceps', 'Brachialis', 'Forearms'
  ],
  'Upper Body - Back': [
    'Lats', 'Upper Back', 'Traps', 'Rhomboids',
    'Rear Delts', 'Lower Back', 'Erector Spinae'
  ],
  'Core': [
    'Abs', 'Obliques', 'Transverse Abdominis',
    'Rectus Abdominis', 'Serratus'
  ],
  'Lower Body': [
    'Quadriceps', 'Hamstrings', 'Glutes',
    'Hip Flexors', 'Adductors', 'Abductors',
    'Calves', 'Gastrocnemius', 'Soleus'
  ],
  'Arms': [
    'Biceps', 'Triceps', 'Forearms', 'Brachialis'
  ],
  'Other': [
    'Rotator Cuff', 'Multiple Muscle Groups',
    'Cardiovascular', 'Endurance'
  ]
};

const MuscleSelector = ({ selectedMuscles, onChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({
    'Upper Body - Front': true,
    'Upper Body - Back': true,
    'Core': true,
    'Lower Body': true,
    'Arms': false,
    'Other': false
  });

  // Parse selected muscles from comma-separated string
  const selected = selectedMuscles ? selectedMuscles.split(', ').filter(Boolean) : [];

  const toggleMuscle = (muscle) => {
    const newSelected = selected.includes(muscle)
      ? selected.filter(m => m !== muscle)
      : [...selected, muscle];
    onChange(newSelected.join(', '));
  };

  const selectAllInGroup = (groupName) => {
    const groupMuscles = MUSCLE_GROUPS[groupName];
    const allSelected = groupMuscles.every(m => selected.includes(m));

    if (allSelected) {
      // Deselect all in group
      const newSelected = selected.filter(m => !groupMuscles.includes(m));
      onChange(newSelected.join(', '));
    } else {
      // Select all in group
      const newSelected = [...new Set([...selected, ...groupMuscles])];
      onChange(newSelected.join(', '));
    }
  };

  const selectAll = () => {
    const allMuscles = Object.values(MUSCLE_GROUPS).flat();
    const allSelected = allMuscles.every(m => selected.includes(m));

    if (allSelected) {
      onChange('');
    } else {
      onChange([...new Set(allMuscles)].join(', '));
    }
  };

  const clearAll = () => {
    onChange('');
  };

  const toggleGroup = (groupName) => {
    setExpandedGroups({
      ...expandedGroups,
      [groupName]: !expandedGroups[groupName]
    });
  };

  const filteredGroups = () => {
    if (!searchTerm) return MUSCLE_GROUPS;

    const filtered = {};
    Object.entries(MUSCLE_GROUPS).forEach(([groupName, muscles]) => {
      const matchingMuscles = muscles.filter(m =>
        m.toLowerCase().includes(searchTerm.toLowerCase())
      );
      if (matchingMuscles.length > 0) {
        filtered[groupName] = matchingMuscles;
      }
    });
    return filtered;
  };

  const groups = filteredGroups();
  const totalSelected = selected.length;

  return (
    <div style={styles.container}>
      {/* Header with search and actions */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.label}>Select Muscles Involved</span>
          <span style={styles.count}>
            {totalSelected} selected
          </span>
        </div>
        <div style={styles.headerRight}>
          <button
            type="button"
            onClick={selectAll}
            style={styles.actionBtn}
          >
            Select All
          </button>
          <button
            type="button"
            onClick={clearAll}
            style={{...styles.actionBtn, ...styles.clearBtn}}
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="Search muscles..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={styles.searchInput}
      />

      {/* Muscle Groups */}
      <div style={styles.groupsContainer}>
        {Object.entries(groups).map(([groupName, muscles]) => {
          const groupSelected = muscles.filter(m => selected.includes(m)).length;
          const allGroupSelected = muscles.every(m => selected.includes(m));
          const isExpanded = expandedGroups[groupName];

          return (
            <div key={groupName} style={styles.group}>
              {/* Group Header */}
              <div style={styles.groupHeader}>
                <button
                  type="button"
                  onClick={() => toggleGroup(groupName)}
                  style={styles.groupToggle}
                >
                  <span style={styles.groupIcon}>
                    {isExpanded ? '▼' : '▶'}
                  </span>
                  <span style={styles.groupName}>{groupName}</span>
                  <span style={styles.groupCount}>
                    {groupSelected}/{muscles.length}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => selectAllInGroup(groupName)}
                  style={{
                    ...styles.groupSelectAll,
                    ...(allGroupSelected ? styles.groupSelectAllActive : {})
                  }}
                >
                  {allGroupSelected ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              {/* Muscle Buttons */}
              {isExpanded && (
                <div style={styles.muscleGrid}>
                  {muscles.map(muscle => {
                    const isSelected = selected.includes(muscle);
                    return (
                      <button
                        key={muscle}
                        type="button"
                        onClick={() => toggleMuscle(muscle)}
                        style={{
                          ...styles.muscleBtn,
                          ...(isSelected ? styles.muscleBtnActive : {})
                        }}
                      >
                        {isSelected && <span style={styles.checkmark}>✓</span>}
                        {muscle}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Selected muscles preview */}
      {totalSelected > 0 && (
        <div style={styles.preview}>
          <div style={styles.previewLabel}>Selected muscles:</div>
          <div style={styles.previewList}>
            {selected.map(muscle => (
              <span key={muscle} style={styles.previewTag}>
                {muscle}
                <button
                  type="button"
                  onClick={() => toggleMuscle(muscle)}
                  style={styles.previewRemove}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: brandColors.mediumGray,
    border: `1px solid ${brandColors.lightGray}`,
    borderRadius: '8px',
    padding: '15px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
    flexWrap: 'wrap',
    gap: '10px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  headerRight: {
    display: 'flex',
    gap: '8px',
  },
  label: {
    color: brandColors.white,
    fontSize: '14px',
    fontWeight: 700,
  },
  count: {
    backgroundColor: brandColors.gold,
    color: brandColors.darkGray,
    padding: '3px 8px',
    borderRadius: '14px',
    fontSize: '12px',
    fontWeight: 700,
  },
  actionBtn: {
    padding: '6px 12px',
    backgroundColor: 'transparent',
    border: `1px solid ${brandColors.gold}`,
    borderRadius: '8px',
    color: brandColors.gold,
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  clearBtn: {
    borderColor: brandColors.silver,
    color: brandColors.silver,
  },
  searchInput: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: brandColors.darkGray,
    border: `1px solid ${brandColors.lightGray}`,
    borderRadius: '8px',
    color: brandColors.white,
    fontSize: '13px',
    marginBottom: '15px',
    boxSizing: 'border-box',
  },
  groupsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    maxHeight: '500px',
    overflowY: 'auto',
    paddingRight: '5px',
  },
  group: {
    backgroundColor: brandColors.darkGray,
    borderRadius: '8px',
    padding: '10px',
    border: `1px solid ${brandColors.lightGray}`,
  },
  groupHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  groupToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    color: brandColors.white,
    cursor: 'pointer',
    padding: '4px',
    fontSize: '14px',
    flex: 1,
  },
  groupIcon: {
    fontSize: '11px',
    color: brandColors.silver,
  },
  groupName: {
    fontWeight: 700,
  },
  groupCount: {
    color: brandColors.gold,
    fontSize: '12px',
    marginLeft: '8px',
  },
  groupSelectAll: {
    padding: '4px 10px',
    backgroundColor: 'transparent',
    border: `1px solid ${brandColors.lightGray}`,
    borderRadius: '4px',
    color: brandColors.silver,
    fontSize: '11px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  groupSelectAllActive: {
    backgroundColor: brandColors.gold,
    borderColor: brandColors.gold,
    color: brandColors.darkGray,
  },
  muscleGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '6px',
    marginTop: '8px',
  },
  muscleBtn: {
    padding: '8px 12px',
    backgroundColor: 'transparent',
    border: `1px solid ${brandColors.lightGray}`,
    borderRadius: '8px',
    color: brandColors.silver,
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  muscleBtnActive: {
    backgroundColor: brandColors.gold,
    borderColor: brandColors.gold,
    color: brandColors.darkGray,
    fontWeight: 700,
  },
  checkmark: {
    fontSize: '11px',
    fontWeight: 700,
  },
  preview: {
    marginTop: '15px',
    padding: '12px',
    backgroundColor: brandColors.darkGray,
    borderRadius: '8px',
    border: `1px solid ${brandColors.lightGray}`,
  },
  previewLabel: {
    color: brandColors.silver,
    fontSize: '11px',
    fontWeight: '600',
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  previewList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  previewTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 8px',
    backgroundColor: brandColors.gold,
    color: brandColors.darkGray,
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '600',
  },
  previewRemove: {
    backgroundColor: 'transparent',
    border: 'none',
    color: brandColors.darkGray,
    fontSize: '16px',
    cursor: 'pointer',
    padding: '0',
    lineHeight: '1',
    fontWeight: 700,
  },
};

export default MuscleSelector;
